import { ButtonView, Plugin } from "ckeditor5"
import { setupConversions } from "./conversion"
import { createDialogHtml, getCurrentDimensions, getDialogValues, type ImageDimensions } from "./dialog"
import "./styles.css"

export class ImageDimensionsPlugin extends Plugin {
  private dialogView: HTMLElement | null = null

  static get pluginName() {
    return "ImageDimensions" as const
  }

  init() {
    this.injectStyles()
    this.setupButton()
    setupConversions(this.editor)
  }

  private injectStyles() {
    if (document.getElementById("ck-image-dimensions-styles")) return
    const styleEl = document.createElement("style")
    styleEl.id = "ck-image-dimensions-styles"
    styleEl.textContent = ""
    document.head.appendChild(styleEl)
  }

  private setupButton() {
    this.editor.ui.componentFactory.add("imageDimensions", (locale: any) => {
      const view = new ButtonView(locale)

      view.set({
        label: "Kích thước",
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 3H3v7h18V3z"/><path d="M21 14H3v7h18v-7z"/></svg>`,
        tooltip: true,
        withText: false,
      })

      view.on("execute", () => this.showDialog())
      return view
    })
  }

  private showDialog() {
    this.removeDialog()

    const editor = this.editor
    const currentDimensions = getCurrentDimensions(editor)
    const container = createDialogHtml(currentDimensions)

    this.setupDialogEvents(container, editor)
    document.body.appendChild(container)
    this.dialogView = container

    const widthInput = container.querySelector("#img-dim-width") as HTMLInputElement | null
    widthInput?.focus()
    if (widthInput?.value) {
      widthInput.select()
    }
  }

  private setupDialogEvents(container: HTMLElement, editor: any) {
    container.querySelector(".ck-image-dimensions-backdrop")?.addEventListener("click", () => this.removeDialog())
    container.querySelector("#img-dim-cancel")?.addEventListener("click", () => this.removeDialog())

    container.querySelector("#img-dim-apply")?.addEventListener("click", () => {
      const { width, height, objectFit } = getDialogValues(container)
      this.applyChanges(editor, { width, height, objectFit })
      this.removeDialog()
    })
  }

  private applyChanges(editor: any, dimensions: ImageDimensions) {
    const selectedElement = editor.model.document.selection.getSelectedElement()

    if (!selectedElement || !["image", "imageInline", "imageBlock"].includes(selectedElement.name)) {
      return
    }

    editor.model.change((writer: any) => {
      const width = dimensions.width ? Number(dimensions.width) : null
      const height = dimensions.height ? Number(dimensions.height) : null

      if (width && width > 0) {
        writer.setAttribute("width", width, selectedElement)
      } else {
        writer.removeAttribute("width", selectedElement)
      }

      if (height && height > 0) {
        writer.setAttribute("height", height, selectedElement)
      } else {
        writer.removeAttribute("height", selectedElement)
      }

      if (dimensions.objectFit) {
        writer.setAttribute("objectFit", dimensions.objectFit, selectedElement)
      } else {
        writer.removeAttribute("objectFit", selectedElement)
      }
    })

    this.applyToView(selectedElement, dimensions)
  }

  private applyToView(element: any, dimensions: ImageDimensions) {
    const viewElement = this.editor.editing.mapper.toViewElement(element)
    if (!viewElement) return

    const targetView = (viewElement as any).is?.("figure")
      ? (viewElement as any).findOne?.("img")
      : viewElement
    if (!targetView) return

    const domElement = typeof (targetView as any).toDOM === "function"
      ? (targetView as any).toDOM(this.editor.editing.view.document)
      : (targetView as any)

    if (!domElement || !(domElement instanceof HTMLElement)) return

    domElement.style.width = dimensions.width && dimensions.height ? `${dimensions.width}px` : ""
    domElement.style.height = dimensions.width && dimensions.height ? `${dimensions.height}px` : ""
    domElement.style.objectFit = dimensions.objectFit || ""
  }

  private removeDialog() {
    if (this.dialogView) {
      document.body.removeChild(this.dialogView)
      this.dialogView = null
    }
  }
}
